import { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import { useController, useFormContext } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import { getError } from '../../../utils/FormError';
import { API_BASE_URL } from '../../../api';

import 'react-quill-new/dist/quill.snow.css';
import './RichTextEditor.scss';

// ── Custom "tip" callout format (registered once, module scope) ────────────
// Renders as <div class="help-tip">...</div> — the public help-article page
// (Help.scss) already styles that exact class, icon included via ::before.
const Quill = ReactQuill.Quill;
const BlockBlot = Quill.import('blots/block');

class TipBlot extends BlockBlot {}
TipBlot.blotName = 'tip';
TipBlot.tagName = 'div';
TipBlot.className = 'help-tip';
Quill.register(TipBlot);

const icons = Quill.import('ui/icons');
icons.tip = '💡';

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const pickImageFile = () => new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
});

const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post(`${API_BASE_URL}/api/v1/backoffice/help/media`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.url;
};

// Best-effort mapping from a mouse (drop) position to a Quill content index —
// there's no public Quill API for this, so we fall back to end-of-document
// when the browser can't resolve a caret position.
const getDropIndex = (editor, x, y) => {
    let node = null;
    let offset = 0;
    if (document.caretRangeFromPoint) {
        const range = document.caretRangeFromPoint(x, y);
        if (range) { node = range.startContainer; offset = range.startOffset; }
    } else if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(x, y);
        if (pos) { node = pos.offsetNode; offset = pos.offset; }
    }
    if (!node) return editor.getLength();

    const blot = Quill.find(node, true);
    if (!blot) return editor.getLength();
    return editor.getIndex(blot) + offset;
};

const RichTextEditor = ({ name, placeholder = '', validationRules = {} }) => {
    const { control, formState: { errors } } = useFormContext();
    const quillRef = useRef(null);
    const wrapperRef = useRef(null);
    // The inline image currently selected in the editor: { index, top, left }
    // (top/left are relative to wrapperRef, used to position the "Changer" button).
    const [selectedImage, setSelectedImage] = useState(null);

    const {
        field: { onChange, value },
    } = useController({
        name,
        control,
        defaultValue: '',
        rules: {
            validate: (val) => stripHtml(val).length > 0 ? true : (validationRules.required || 'Ce champ est requis'),
        },
    });

    // Opens a file picker, uploads the chosen image to the same endpoint the
    // rich-text article screenshots are uploaded through, then embeds it at
    // the current cursor position.
    const handleImageInsert = async () => {
        const file = await pickImageFile();
        if (!file) return;

        const editor = quillRef.current?.getEditor();
        const range = editor?.getSelection(true);
        const index = range ? range.index : editor.getLength();

        try {
            const url = await uploadImage(file);
            editor.insertEmbed(index, 'image', url, 'user');
            editor.setSelection(index + 1);
        } catch (e) {
            toast.error("Erreur lors de l'envoi de l'image");
        }
    };

    // Replaces the currently selected inline image (clicked by the user) with
    // a newly uploaded one, keeping its position in the content and its
    // existing caption, if any.
    const handleImageReplace = async () => {
        const target = selectedImage;
        setSelectedImage(null);
        if (!target) return;

        const file = await pickImageFile();
        if (!file) return;

        const editor = quillRef.current?.getEditor();
        try {
            const url = await uploadImage(file);
            editor.deleteText(target.index, 1, 'user');
            editor.insertEmbed(target.index, 'image', url, 'user');
            if (target.caption) editor.formatText(target.index, 1, 'alt', target.caption, 'user');
            editor.setSelection(target.index + 1);
        } catch (e) {
            toast.error("Erreur lors de l'envoi de l'image");
        }
    };

    // Persists the caption typed for the selected image as its `alt`
    // attribute — a format Quill's image blot natively supports. The public
    // help-article page (useArticleToc in HelpPost.js) turns any <img alt>
    // into a visible <figure><figcaption> under the image.
    const commitCaption = (value) => {
        if (!selectedImage) return;
        const editor = quillRef.current?.getEditor();
        editor.formatText(selectedImage.index, 1, 'alt', value.trim(), 'user');
    };

    // Toggles the "tip" callout format on the current line/selection.
    const handleTipToggle = () => {
        const editor = quillRef.current?.getEditor();
        const range = editor?.getSelection(true);
        if (!range) return;

        const [line] = editor.getLine(range.index);
        const isTip = line?.domNode?.classList?.contains('help-tip');
        editor.formatLine(range.index, range.length || 1, 'tip', !isTip, 'user');
    };

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [2, 3, false] }],
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['tip'],
                ['link', 'image'],
                ['clean'],
            ],
            handlers: { image: handleImageInsert, tip: handleTipToggle },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    // Click-to-select an inline image: shows a "Changer l'image" button
    // positioned over it. Clicking anywhere else clears the selection.
    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const onClick = (e) => {
            if (e.target.tagName !== 'IMG') {
                setSelectedImage(null);
                return;
            }
            const blot = Quill.find(e.target);
            const index = blot ? editor.getIndex(blot) : null;
            if (index == null) return;

            const imgRect = e.target.getBoundingClientRect();
            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            setSelectedImage({
                index,
                top: imgRect.top - wrapperRect.top,
                left: imgRect.left - wrapperRect.left,
                bottom: imgRect.bottom - wrapperRect.top,
                caption: e.target.getAttribute('alt') || '',
            });
        };

        editor.root.addEventListener('click', onClick);
        return () => editor.root.removeEventListener('click', onClick);
    }, []);

    // Paste or drag-and-drop an image straight into the editor — the primary
    // content type here is screenshots, so this avoids the round-trip of
    // saving a clipboard image to disk just to re-pick it via the toolbar.
    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const insertAt = async (file, index) => {
            try {
                const url = await uploadImage(file);
                editor.insertEmbed(index, 'image', url, 'user');
                editor.setSelection(index + 1);
            } catch (e) {
                toast.error("Erreur lors de l'envoi de l'image");
            }
        };

        const onPaste = (e) => {
            const item = Array.from(e.clipboardData?.items || []).find((it) => it.type.startsWith('image/'));
            if (!item) return; // not an image — let Quill handle normal text/HTML paste
            e.preventDefault();
            const file = item.getAsFile();
            if (!file) return;
            const range = editor.getSelection(true);
            insertAt(file, range ? range.index : editor.getLength());
        };

        const onDragOver = (e) => {
            if (Array.from(e.dataTransfer?.items || []).some((it) => it.kind === 'file')) {
                e.preventDefault();
            }
        };

        const onDrop = (e) => {
            const file = Array.from(e.dataTransfer?.files || []).find((f) => f.type.startsWith('image/'));
            if (!file) return;
            e.preventDefault();
            e.stopPropagation();
            insertAt(file, getDropIndex(editor, e.clientX, e.clientY));
        };

        editor.root.addEventListener('paste', onPaste);
        editor.root.addEventListener('dragover', onDragOver);
        editor.root.addEventListener('drop', onDrop);
        return () => {
            editor.root.removeEventListener('paste', onPaste);
            editor.root.removeEventListener('dragover', onDragOver);
            editor.root.removeEventListener('drop', onDrop);
        };
    }, []);

    // Also dismiss the overlay when clicking anywhere outside the editor.
    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setSelectedImage(null);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const error = getError(errors, name);

    return (
        <div className={`rich-text-editor ${error ? 'is-invalid' : ''}`} ref={wrapperRef}>
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value || ''}
                onChange={onChange}
                placeholder={placeholder}
                modules={modules}
                // Quill 2's getSemanticHTML() (the default serializer) has a
                // known bug: it converts every regular space to &nbsp;, which
                // then can't wrap and overflows the article's container. Use
                // the plain contentEditable DOM HTML instead — it doesn't
                // have this issue and still captures everything (images,
                // formatting, the custom tip blot, etc.).
                useSemanticHTML={false}
            />
            {selectedImage && (
                <>
                    <button
                        type="button"
                        className="rich-text-editor__replace-image"
                        style={{ top: selectedImage.top + 8, left: selectedImage.left + 8 }}
                        onClick={handleImageReplace}
                    >
                        <FontAwesomeIcon icon={faImage} /> Changer l'image
                    </button>
                    <div
                        className="rich-text-editor__caption-box"
                        style={{ top: selectedImage.bottom + 8, left: selectedImage.left }}
                    >
                        <input
                            key={selectedImage.index}
                            type="text"
                            className="rich-text-editor__caption-input"
                            defaultValue={selectedImage.caption}
                            placeholder="Légende affichée sous l'image sur le site public..."
                            onBlur={(e) => commitCaption(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    commitCaption(e.target.value);
                                    e.target.blur();
                                }
                            }}
                        />
                    </div>
                </>
            )}
            {error && <div className="invalid-feedback d-block">{error.message}</div>}
        </div>
    );
};

export default RichTextEditor;
